import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import {
    ApplicationInsights,
    IPageViewTelemetry,
    ITelemetryItem,
    SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';

@Injectable({
    providedIn: 'root',
})
export class AppInsightsService {
    private angularPlugin = new AngularPlugin();
    router = inject(Router);
    private appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: environment.appInsights.key,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            extensions: [this.angularPlugin],
            extensionConfig: {
                [this.angularPlugin.identifier]: {
                    router: this.router,
                },
            },
        },
    });
    constructor() {
        if (environment.appInsights.enableTracking) {
            this.appInsights.loadAppInsights();
            this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
                envelope.tags = envelope.tags ?? [];
            });
        }
    }

    // expose methods that can be used in components and services
    logEvent(name: string, props: Record<string, object>): void {
        this.appInsights.trackEvent({ name, properties: this.getPropertyString(props) });
    }

    logTrace(message: string, props: Record<string, object>): void {
        this.appInsights.trackTrace({
            message,
            properties: this.getPropertyString(props),
        });
    }

    logPageView(view: IPageViewTelemetry) {
        this.appInsights.trackPageView(view);
    }

    logException(exception: Error, severityLevel: SeverityLevel) {
        this.appInsights.trackException({
            exception: exception,
            severityLevel: severityLevel,
        });
    }
    logMetric(name: string, average: number, props: Record<string, object>) {
        this.appInsights.trackMetric(
            { name: name, average: average },
            this.getPropertyString(props),
        );
    }

    private getPropertyString(props: Record<string, object>) {
        const properties = Object.entries(props).map(([key, value]) => {
            return { [key]: value };
        });

        return properties;
    }
}
